// =============================================================================
// ESPOLIDAR WEB ADMIN - AUTH CONTEXT
// =============================================================================

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { authApi } from '../services/api'
import { wsService } from '../services/websocket'
import type { User, LoginCredentials } from '../types'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token')
      if (token) {
        try {
          const userData = await authApi.getMe()
          setUser(userData)
          wsService.connect()
        } catch {
          localStorage.removeItem('token')
        }
      }
      setIsLoading(false)
    }
    checkAuth()

    return () => {
      wsService.disconnect()
    }
  }, [])

  const login = async (credentials: LoginCredentials) => {
    const { access_token } = await authApi.login(credentials)
    localStorage.setItem('token', access_token)
    const userData = await authApi.getMe()
    setUser(userData)
    wsService.connect()
    navigate('/')
  }

  const logout = () => {
    localStorage.removeItem('token')
    setUser(null)
    wsService.disconnect()
    navigate('/login')
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
